import { useEffect, useMemo, useState } from 'react';
import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import { useGameSelector, useInputBindings, useIsTourRunning } from '@sorrem-client/ui/hooks';
import { formatRunHint } from '@sorrem-client/ui/shared/run-hint-text';
import styles from './RunHintBanner.module.scss';

/** How long a hint stays up, unless another replaces it. */
export const RUN_HINT_SHOWN_MS = 12_000;

/**
 * The run's hint banner (#233): the host's latest run hint, with the
 * player's own keys named, for a few seconds. Info toned, apart from the
 * warning toasts, and it never takes the pointer, so play goes on under it.
 * A hint that comes while a guided tour is up (the intro's HUD tour, #234)
 * waits for it to end, then shows its full time.
 */
export function RunHintBanner(): JSX.Element | null {
    const { bridge } = useGameplay();
    const hint = useGameSelector(bridge, (state) => state.runHint);
    const { bindings } = useInputBindings();
    const isTourRunning = useIsTourRunning();
    const [hiddenId, setHiddenId] = useState<string | null>(null);

    useEffect(() => {
        if (hint === null || isTourRunning) {
            return undefined;
        }
        const timer = window.setTimeout(() => setHiddenId(hint.id), RUN_HINT_SHOWN_MS);
        return (): void => window.clearTimeout(timer);
    }, [hint, isTourRunning]);

    const text = useMemo(() => (hint === null ? '' : formatRunHint(hint.text, bindings)), [hint, bindings]);

    if (hint === null || hiddenId === hint.id || isTourRunning) {
        return null;
    }
    return (
        <div key={hint.id} className={styles.runHintBanner} role="status" aria-live="polite">
            {text}
        </div>
    );
}
